import { useState, useEffect, useContext } from 'react';

import {
  Grid,
  Typography,
  TextField,
  InputLabel,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Button
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SaveIcon from '@material-ui/icons/Save';
import { useHistory, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { ViewerComponent } from '../commons/viewer/viewer.component';
import { getCategories } from '../../services/categories.service';
import { uploadImage } from '../../services/images.service';
import { useStyles } from './products-edit.styles';
import { updateProduct, getProduct } from '../../services/products.service';
import { BackdropComponent } from '../commons/backdrop/backdrop.component';
import { ProductsContext } from '../../context/products.context';

export const ProductsEditComponent = () => {

  const history = useHistory();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams();
  const productsContext = useContext(ProductsContext);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [product, setProduct] = useState({
    name: '',
    category: '',
    price: '',
    description: '',
    image: ''
  });

  const fetchCategories = async () => {
    if (productsContext.categories.length === 0) {
      const categoriesItems = await getCategories();
      productsContext.handleCategories(categoriesItems);
      setCategories(categoriesItems);
    } else {
      setCategories(productsContext.categories);
    }
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }

  useEffect(() => {
    const fetchProduct = async (productId) => {
      const productItem = await getProduct(productId);
      setProduct(productItem);
    }
    fetchProduct(params.id);
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setProduct({
      ...product,
      [name]: value
    });
  }

  const handleFile = async (event) => {
    try {
      setUploading(true);
      const file = event.target.files[0];
      const filename = `${Date.now()}-${file.name}`;
      const imageUrl = await uploadImage(filename, file);
      setProduct({
        ...product,
        image: imageUrl
      });
      enqueueSnackbar(`${file.name} agregado correctamente`, {
        variant: 'success',
        resumeHideDuration: 3000,
        anchorOrigin:
          { horizontal: 'right', vertical: 'bottom' },
      });
      setUploading(false);
    } catch (err) {
      enqueueSnackbar(JSON.stringify(err), {
        variant: 'error',
        resumeHideDuration: 6000,
        anchorOrigin:
          { horizontal: 'right', vertical: 'bottom' },
      });
      setUploading(false);
    }
  }

  const save = async () => {
    try {
      if (isNaN(+product.price)) {
        enqueueSnackbar('Precio debe ser número', {
          variant: 'error',
          resumeHideDuration: 4000,
          anchorOrigin:
            { horizontal: 'right', vertical: 'bottom' },
        });
        return;
      }
      const productToAdd = {
        ...product,
        price: +product.price,
        enable: true
      };
      const updatedProduct = await updateProduct(params.id, productToAdd);
      productsContext.handleProducts(
        productsContext.products.map(p => p.id === updatedProduct.id ? updatedProduct : p)
      );
      enqueueSnackbar(`${product.name} editado correctamente`, {
        variant: 'success',
        resumeHideDuration: 3000,
        anchorOrigin:
          { horizontal: 'right', vertical: 'bottom' },
      });
      history.push('/products');
    } catch (err) {
      enqueueSnackbar(JSON.stringify(err), {
        variant: 'error',
        resumeHideDuration: 6000,
        anchorOrigin:
          { horizontal: 'right', vertical: 'bottom' },
      });
    }
  }

  return loading ? (
    <BackdropComponent />
  ) : (
    <ViewerComponent>
      <Grid container justifyContent="center" spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h3" align="center">
            Editar producto
          </Typography>
        </Grid>
        <Grid item xs={10}>
          <TextField
            id="name"
            name="name"
            label="Nombre del producto"
            variant="outlined"
            onChange={handleChange}
            defaultValue={product.name}
            fullWidth
          />
        </Grid>
        <Grid item xs={10}>
          <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={12} lg={6}>
              <Autocomplete
                id="categories-list"
                loading={loading}
                loadingText="Cargando..."
                options={categories}
                defaultValue={categories && categories.find(category => category.id === product.category)}
                onChange={(event, value) => setProduct({ ...product, category: value?.id })}
                getOptionLabel={(option) => option.name}
                style={{ width: '100%' }}
                renderInput={(params) => <TextField {...params} label="Categorias" variant="outlined" />}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="price">Precio</InputLabel>
                <OutlinedInput
                  id="price"
                  name="price"
                  defaultValue={product.price}
                  startAdornment={<InputAdornment position="start">$</InputAdornment>}
                  labelWidth={60}
                  onChange={handleChange}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={10}>
          <TextField
            id="description"
            name="description"
            label="Descripción"
            multiline
            rows={4}
            variant="outlined"
            onChange={handleChange}
            fullWidth
            defaultValue={product.description}
          />
        </Grid>
        <Grid item xs={10}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <input
                accept="image/*"
                className={classes.input}
                id="image"
                type="file"
                onChange={handleFile}
              />
              <label htmlFor="image">
                <Button disabled={uploading} variant="contained" color="primary" component="span">
                  Subir foto
                </Button>
              </label>
            </Grid>
            {product.image && (
              <Grid item xs={4}>
                <img className={classes.image} src={product.image} alt="imagen" />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={10}>
          <Grid container justifyContent="flex-end" spacing={2}>
            <Grid item>
              <Button
                variant="contained"
                color="default"
                startIcon={<ArrowBackIcon />}
                onClick={() => history.goBack()}
              >
                Regresar
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={save}
                disabled={!product.name || !product.category || !product.price || uploading}
              >
                Guardar
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </ViewerComponent>
  )
}